import React, { useState, useEffect } from "react";
import { MdCategory } from "react-icons/md";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import SpecificAPI from "../../services/SpecificApi";
import { PromptCategoryCard } from "views/Components/PromptCategoryCard";
import useLanguage from "services/useLanguage";
import { ClipLoader } from "react-spinners";
import { MdChat } from "react-icons/md";
import { MdArrowBack } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import localization from "services/localization";
import { useUserStore } from "context/useUserStore";
import { FormBuilderModal } from "views/Components/FormBuilder/form-builder-modal";
import { usePromptCategoriesStore } from "context/usePromptCategoriesStore";
import { usePromptsStore } from "context/usePromptsStore";
import { useDepartmentsStore } from "context/useDepartmentsStore";
import { toast } from "react-toastify";
import LoadingSpinner from "views/Components/LoadingSpinner";
import { useLanguageStore } from "context/useLanguageStore";

const PromptDashboard = () => {
  const { currentLang } = useLanguageStore();
  const navigate = useNavigate();
  const { departmentId, moduleId } = useParams();

  const { appUser, userData } = useUserStore();
  const { promptCategories, isPromptCategoriesLoading } =
    usePromptCategoriesStore();
  const { prompts, isPromptsLoading } = usePromptsStore();
  const { departments, isDepartmentsLoading, setDepartments } =
    useDepartmentsStore();

  const [loading, setLoading] = useState(false);
  const [modulePromptCategories, setModulePromptCategories] = useState([]);
  const role = appUser?.role || null;
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [selectedPromptData, setSelectedPromptData] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [selectedPromptTemplate, setSelectedPromptTemplate] = useState(null);

  useEffect(() => {
    if (!isPromptCategoriesLoading && moduleId) {
      const filteredPromptCategories = promptCategories
        .filter((c) => c.module_id == moduleId)
        .sort((a, b) => {
          const getTitle = (cat) =>
            cat.title?.[currentLang] ||
            cat.title?.["It"] ||
            cat.title?.["En"] ||
            "";
          return getTitle(a)
            .toLowerCase()
            .localeCompare(getTitle(b).toLowerCase());
        });
      setModulePromptCategories(filteredPromptCategories);
    }
  }, [moduleId, promptCategories, isPromptCategoriesLoading]);

  useEffect(() => {
    getStudents();
  }, []);

  const getStudents = async () => {
    try {
      const response = await SpecificAPI.getAllUsers({ role: "student" });
      const studentsData = response || [];
      setStudents(studentsData);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  // const handlePromptClick = async (prompt) => {
  //   setSelectedPromptData(prompt);
  //   setLoadingTemplate(true);
  //   const templateId = prompt?.prompt_template_id;
  //   const response = await SpecificAPI.getPromptTemplateById(templateId);
  //   setSelectedPromptTemplate(response);

  //   // const historyParams =
  //   //   appUser.role === "student"
  //   //     ? { department_id: departmentId, privacy: "public" }
  //   //     : { department_id: departmentId, user_id: appUser.id };

  //   // var p0 = await SpecificAPI.getAllHistory(historyParams);

  //   // const history = p0.find((h) => h.prompt_id === prompt.id);

  //   const apiFields = response?.system_props?.viz_props;
  //   if (apiFields == null || apiFields.length == 0) {
  //     // if(history){
  //     //   navigate(`/edu-prompt/history/module/${moduleId}/${departmentId}/${history.id}`);
  //     // }else{
  //     addPromptMessage(
  //       {},
  //       null,
  //       prompt.id,
  //       response?.system_props?.chat_visible_to_members,
  //     );
  //     // }
  //   } else {
  //     setShowPromptModal(true);
  //   }
  // };

  const handlePromptClick = async (prompt) => {
    try {
      setSelectedPromptData(prompt);
      setLoadingTemplate(true);
      setSelectedPromptTemplate(null); // reset previous template

      const templateId = prompt?.prompt_template_id;
      const response = await SpecificAPI.getPromptTemplateById(templateId);

      setSelectedPromptTemplate(response);

      const apiFields = response?.system_props?.viz_props;

      if (!apiFields || apiFields.length === 0) {
        await addPromptMessage(
          {},
          null,
          prompt.id,
          response?.system_props?.chat_visible_to_members,
          response?.type,
        );
      } else {
        setShowPromptModal(true);
      }
    } catch (error) {
      console.error("Failed to load prompt template:", error);
      toast.error("Failed to load prompt template");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleFavoriteToggle = (promptId) => {
    // setPrompts((prev) =>
    //     prev.map((p) =>
    //         p.id === promptId ? { ...p, isFavorite: !p.isFavorite } : p
    //     )
    // );
  };

  const handleGoToChat = () => {
    if (role == "student") {
      navigate(`/edu-prompt/history/department/${departmentId}`);
    } else {
      navigate(`/edu-prompt/history/module/${moduleId}/${departmentId}`);
    }
  };

  const addPromptMessage = async (
    formData,
    selectedAttachment,
    promptId,
    chat_visible_to_members,
    type,
  ) => {
    try {
      setLoading(true);
      const isDiscussionTopic =
        selectedPromptData?.title?.En === "test" ||
        selectedPromptData?.title?.It === "test";

      // 1. Create history
      const newHistory = {
        user_id: appUser.id,
        title: formData?.chat_title || "New Session",
        privacy: chat_visible_to_members ? "public" : "private",
        prompt_id: promptId,
        prompt_data: {
          ...formData,
          resource_id: selectedAttachment || null,
        },
        department_id: departmentId,
      };

      const response = await SpecificAPI.createHistory(
        newHistory,
        userData?.selected_school_id,
      );
      const createdHistoryId = response?.data?.id;

      if (!createdHistoryId) {
        console.error("Failed to create history: No ID returned");
        return;
      }
      const tempId = `temp-${Date.now()}`;
      let tempMessage = {
        id: tempId,
        user_id: appUser.id,
        history_id: createdHistoryId,
        prompt_id: promptId,
        question: formData.message,
        saved: false,
        response: null,
        loading: true,
        created_at: moment().toISOString(),
        type: type,
      };
      navigate(
        `/edu-prompt/history/module/${moduleId}/${departmentId}/${createdHistoryId}`,
        { state: { tempMessage } }, // pass temp message
      );

      // // 2. Create initial message BEFORE navigating
      // const msg = {
      //   user_id: appUser.id,
      //   prompt_id: promptId,
      //   history_id: createdHistoryId,
      //   question: formData.message,
      //   saved: false,
      //   // prompt_template_id: selectedPromptData?.prompt_template_id,
      //   timestamp: moment().toISOString(),
      // };

      // try {
      //   await SpecificAPI.createMessage(msg,userData?.selected_school_id);
      // } catch (msgError) {
      //   console.error("Error sending initial message:", msgError);
      // }

      // // 3. Navigate AFTER message creation
      // navigate(
      //   `/edu-prompt/history/module/${moduleId}/${departmentId}/${createdHistoryId}`,
      // );
    } catch (error) {
      console.error("Error creating history:", error);
      toast.error(
        error?.response?.data?.error ||
          localization.failed_to_create_message ||
          "Failed to create message!",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!departments?.length || !students?.length || !departmentId) return;
    const members = departments.find((d) => d.id == departmentId)?.members;
    const filteredStudentsList = students?.filter((s) =>
      members?.includes(s.id),
    );
    setFilteredStudents(filteredStudentsList);
  }, [departments, students, departmentId]);

  if (isPromptCategoriesLoading || isPromptsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 min-h-screen main-page page-top-padding">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/30 z-50 flex items-center justify-center">
          <ClipLoader color="var(--primary-icon)" size={50} />
        </div>
      )}
      {/* Header */}
      <div className="d-flex justify-content-center mb-5">
        <div
          className="position-relative d-flex align-items-center justify-content-center w-100"
          style={{ maxWidth: "1200px" }}
        >
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="tooltip-back">
                {role === "teacher" ? "Back to Classroom" : "Back to Modules"}
              </Tooltip>
            }
          >
            <span className="position-absolute start-0">
              <MdArrowBack
                size={28}
                className="icon-btn"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/services/${departmentId}`)}
              />
            </span>
          </OverlayTrigger>

          {/* Centered Title */}
          <div className="d-flex align-items-center gap-3">
            <div className="title-icon-bg d-flex h-12 w-12 items-center justify-center rounded-xl shadow-md transition-colors duration-200">
              <MdCategory
                size={28}
                className="transition-colors duration-200 title-icon"
              />
            </div>

            <h1 className="text-3xl font-bold m-0 page-title">
              {localization.promptCategories || "Prompt Categories"}
            </h1>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="d-flex justify-content-center">
        <div className="row g-2" style={{ maxWidth: "1200px" }}>
          {modulePromptCategories.map((cat) => {
            const sortedPrompts = prompts
              .filter((p) => p.prompt_category_id === cat.id)
              .sort((a, b) => {
                const getSortKey = (p) =>
                  p.prompt_template_name?.toLowerCase() ||
                  (
                    p.title[currentLang] ||
                    p.title["It"] ||
                    p.title["En"] ||
                    ""
                  ).toLowerCase();

                return getSortKey(a).localeCompare(getSortKey(b));
              });
            return (
              <div
                key={cat.id}
                className="col-12 col-md-12 col-lg-12 d-flex align-items-start mb-4"
              >
                <PromptCategoryCard
                  category={cat}
                  prompts={sortedPrompts}
                  onPromptClick={handlePromptClick}
                  onFavoriteToggle={handleFavoriteToggle}
                  style={{ width: "100%" }}
                  setShowPromptModal={setShowPromptModal}
                />
              </div>
            );
          })}
        </div>
      </div>

      <button
        onClick={handleGoToChat}
        title="Go to Chat"
        className="
      action-btn primary fixed bottom-4 right-4
      p-4 rounded-full text-white
      shadow-2xl z-50
      flex items-center justify-center
      transform transition-all duration-300
      hover:scale-110 hover:-translate-y-1
      hover:shadow-orange-500/60
      animate-pulse-slow
    "
        aria-label="Go to Chat"
      >
        <MdChat size={24} />
      </button>
      {/* <PromptDetailModal open={showPromptModal} data={selectedPromptData} onClose={() => setShowPromptModal(false)} onGeneratePressed={addPromptMessage} theme={theme} loading={loading} /> */}
      <FormBuilderModal
        isOpen={showPromptModal && !!selectedPromptTemplate}
        onClose={() => setShowPromptModal(false)}
        // fields={[]}
        templateId={selectedPromptData?.prompt_template_id}
        promptId={selectedPromptData?.id}
        title={selectedPromptData?.title[currentLang]}
        description={selectedPromptData?.description[currentLang]}
        onSubmit={addPromptMessage}
        submitButtonLabel="Create"
        students={filteredStudents}
        selectedPromptTemplate={selectedPromptTemplate}
      />
    </div>
  );
};

export default PromptDashboard;
